import Joyride from "react-joyride";
import React, { useState, useRef, useEffect, useContext, useMemo, useCallback } from "react";
import { GlobalStyles } from "@mui/material";
import axiosInstance from "api/axiosInstance";
import StrategyKPICharts from "./StrategyKPICharts";
import Grid from "@mui/material/Grid";
import { toast } from "react-toastify";
import MenuItem from "@mui/material/MenuItem";
import CircularProgress from "@mui/material/CircularProgress";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import Fade from "@mui/material/Fade";
import Slide from "@mui/material/Slide";
import Grow from "@mui/material/Grow";
import Collapse from "@mui/material/Collapse";
import TextField from "@mui/material/TextField";
import Chip from "@mui/material/Chip";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { useTheme, alpha } from "@mui/material/styles";

import Card from "@mui/material/Card";
import ExampleFormulaDialog from "./ExampleFormulaDialog";
import StrategyGroupDialog from "./StrategyGroupDialog";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { trackEvent } from "utils/analytics";
import { LanguageContext } from "../../../../context/LanguageContext";

import coingraphIcon from "../../../../icon/coingraph.png";
import coinstrategyIcon from "../../../../icon/coinstrategy.png";
import downloadIcon from "../../../../icon/download.png";
import graphIcon from "../../../../icon/graph.png";
import mascoinIcon from "../../../../icon/mascoin.png";
import strategyIcon from "../../../../icon/strategy.png";
import { COLORS } from "theme/tokens";
import { getSummaryDisplay } from "utils/aiSummarySections";
import { rescaleBalances } from "utils/equityBaseline";
// DEMO 頁(免登入)用：本地範例/策略組/假回測，避免打需登入的 API（僅 demoMode 分支用到）
import demoLogicExamples from "../strategydemo/logicExamples.json";
import demoLogicGroups from "../strategydemo/logicGroupExamples.json";
import demoFakeKPI from "../strategydemo/fakeKPI.json";
// fakeKPI.json 的 annual_pnl_usd 是以這個本金做的「期末已平倉餘額」（DEMO 回測時依表單本金縮放，見 handleBacktest）
const DEMO_FAKE_KPI_CAPITAL = 10000;

const getYesterdayStr = () => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  const tzoffset = d.getTimezoneOffset() * 60000;
  return new Date(d - tzoffset).toISOString().split('T')[0];
};

const getMinDateStr = (tf) => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  if (tf === "1d") {
    d.setFullYear(d.getFullYear() - 5);
  } else {
    d.setMonth(d.getMonth() - 1);
  }
  const tzoffset = d.getTimezoneOffset() * 60000;
  return new Date(d - tzoffset).toISOString().split('T')[0];
};

// ⚠️ 2026-09-18：拿掉「雙向(BOTH)」。
//   原因：表單只有**一組**進出場條件，而系統從頭到尾沒有定義「雙向」是什麼意思 ——
//   摘要 prompt 只塞一句「下單類型：雙向」、AI_modules 對方向零字、生成回測碼的 prompt
//   完全沒提 BOTH。結果是 AI 每次自己發明一套（忽略它只做多／自己加反手邏輯／兩次兩種寫法），
//   而這份碼會被打包進 EXE 用真錢下單。AI 自己的摘要就露餡過：「KD 低於 20 時建立多空部位」
//   —— 同一個訊號不可能同時開多又開空。
//   選項本體在 DB 的 system_option(type='DIRECTION')，已把 BOTH 設成 isVisible=0；
//   但這份是 useState 的初始值、API 失敗時的 fallback、以及 demo 模式的來源，
//   三條路都繞過 DB，所以必須一起拿掉，否則「隱藏」只做了一半。
//   要恢復的話：先把「雙向」的語意定義清楚並寫進生成 prompt（見同日討論的 B/C 方案）。
const DEFAULT_DIRECTIONS = [
  { value: "LONG", label: "Long" },
  { value: "SHORT", label: "Short" }
];

const ORANGE_PRIMARY = "#f16b2a";
const ORANGE_GRADIENT = "linear-gradient(90deg, #f88e2b 0%, #d35400 100%)";

const StatusCard = ({ title, count, total, unit, icon, remainingLabel }) => (
  <Card sx={{
    borderRadius: "12px",
    p: 2.5,
    height: "100%",
    position: "relative",
    boxShadow: "0 4px 12px 0 rgba(0,0,0,0.03)",
    border: "1px solid rgba(0,0,0,0.04)"
  }}>
    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
      <Box sx={{
        width: 60,
        height: 60,
        padding: "9px",
        bgcolor: COLORS.grayLightest,
        borderRadius: "12px",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        aspectRatio: "1/1"
      }}>
        <img src={icon} width="42" height="42" style={{ padding: "3px" }} alt={title} />
      </Box>
      <Box textAlign="right">
        <MDTypography sx={{ fontSize: "12px", fontWeight: 500, color: COLORS.black, lineHeight: "normal" }}>
          {remainingLabel}
        </MDTypography>
      </Box>
    </Box>
    <Box display="flex" justifyContent="space-between" alignItems="flex-end">
      <MDTypography sx={{ color: COLORS.black, fontSize: "16px", fontWeight: 500, lineHeight: "normal" }}>
        {title}
      </MDTypography>
      <Box textAlign="right">
        <MDTypography component="span" sx={{ color: COLORS.main, fontSize: "32px", fontWeight: 500, fontFamily: "Outfit, \"Noto Sans TC\", sans-serif", lineHeight: "36px" }}>
          {count}
        </MDTypography>
        {total !== undefined && (
          <MDTypography component="span" sx={{ color: COLORS.black, fontSize: "16px", fontWeight: 500, fontFamily: "Outfit, \"Noto Sans TC\", sans-serif" }}>
            / {total}
          </MDTypography>
        )}
        {unit && (
          <MDTypography component="span" sx={{ ml: 0.5, color: COLORS.black, fontSize: "16px", fontWeight: 500 }}>
            {unit}
          </MDTypography>
        )}
      </Box>
    </Box>
  </Card>
);

// 2026-06-05 標籤化輸入框：保留 chip（可 X 一鍵刪）+ 加自行打字框（打字按 Enter 也變 chip）
//   value: 字串陣列；onChange: setter（支援 prev => 函式）；placeholder / maxLength 同舊版
function TagInput({ value, onChange, placeholder, maxLength, error }) {
  const [input, setInput] = useState("");
  // 外框 minHeight 100px 只是外觀，真正的 <input> 只有一行高且靠上；
  // 不轉焦點的話，框內其餘空白處點了沒反應 → 點框內任一處都聚焦到輸入框。
  const inputRef = useRef(null);
  const currentLength = value.map((v) => `(${v})`).join("").length;
  const isOverLimit = currentLength > maxLength;

  const addTag = () => {
    const t = input.trim();
    if (!t) return;
    onChange((prev) => [...prev, t]);
    setInput("");
  };

  return (
    <Box
      onMouseDown={(e) => {
        // 只處理「點在空白處」；點 chip / 刪除鈕 / input 本身時不介入，保留原本行為與文字選取
        if (e.target !== e.currentTarget) return;
        e.preventDefault(); // 不讓 mousedown 造成的預設行為搶走焦點
        inputRef.current?.focus();
      }}
      sx={{
        minHeight: "100px",
        bgcolor: COLORS.grayLightest,
        borderRadius: "10px",
        p: 1.5,
        display: "flex",
        flexWrap: "wrap",
        gap: 1,
        alignContent: "flex-start",
        border: isOverLimit ? "2px solid #f44336" : error ? "1px solid #f88e2b" : "1px solid transparent",
        transition: "all 0.2s ease",
        cursor: "text",
      }}
    >
      {value.map((text, idx) => (
        <Chip
          key={idx}
          label={text}
          onDelete={() => onChange((prev) => prev.filter((_, i) => i !== idx))}
          sx={{
            bgcolor: COLORS.white,
            border: "1px solid #e0e0e0",
            fontWeight: 500,
            fontSize: "0.85rem",
            "& .MuiChip-deleteIcon": {
              color: COLORS.main,
              "&:hover": { color: COLORS.main },
            },
          }}
        />
      ))}
      {/* 自行打字輸入：Enter 或失焦時把文字加成一個 chip */}
      <input
        ref={inputRef}
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            addTag();
          }
        }}
        onBlur={addTag}
        placeholder={placeholder}
        style={{
          flex: "1 1 140px",
          minWidth: "140px",
          border: "none",
          outline: "none",
          background: "transparent",
          fontSize: "0.9rem",
          padding: "4px 2px",
          color: COLORS.black,
        }}
      />
    </Box>
  );
}

export default function StrategySummaryForm({ demoMode = false }) {
  const { componentLanguageData, loadLanguageFile, language } = useContext(LanguageContext);

  // ✅ 區塊標題組件 (Requirement 2 & 3)
  const SectionHeader = ({ title, hint, mb = 2 }) => (
    <Box display="flex" alignItems="center" mb={mb} mt={1}>
      <Box sx={{ width: "4px", height: "16px", bgcolor: COLORS.main, mr: 1, borderRadius: "2px" }} />
      <MDTypography sx={{ color: COLORS.black, fontSize: "16px", fontWeight: 700, lineHeight: "normal" }}>
        {title}
      </MDTypography>
      {hint && (
        <MDTypography sx={{ color: COLORS.main, fontSize: "14px", fontWeight: 400, ml: 1, lineHeight: "normal" }}>
          {hint}
        </MDTypography>
      )}
    </Box>
  );

  // ✅ 內容標題組件 (Requirement 4)
  const SectionLabel = ({ title, onClick, showButton = true }) => (
    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
      <MDTypography sx={{ color: COLORS.black, fontSize: "14px", fontWeight: 400, lineHeight: "normal" }}>
        {title}
      </MDTypography>
      {showButton && (
        <MDButton
          size="small"
          onClick={onClick}
          sx={{
            bgcolor: "#f88e2b !important",
            color: "#ffffff !important",
            borderRadius: "10px",
            fontSize: "0.75rem",
            padding: "2px 10px",
            minHeight: "auto",
            textTransform: "none",
            "&:hover": { bgcolor: "#d35400 !important" }
          }}
        >
          {LANGUAGE.Main.Content22 || "帶入範例"}
        </MDButton>
      )}
    </Box>
  );

  useEffect(() => {
    loadLanguageFile(
      "StrategySummaryForm",
      "strategy/StrategySummaryForm/StrategySummaryForm_lang"
    );
  }, [language, loadLanguageFile]);

  const LANGUAGE = useMemo(() => {
    return componentLanguageData.StrategySummaryForm || { Main: {}, Error: {}, Guided: {} };
  }, [componentLanguageData]);

  const navigate = useNavigate();
  const [symbol, setSymbol] = useState("EURUSD");
  const [orderType, setOrderType] = useState("LONG");
  const [timeframe, setTimeframe] = useState("1d");
  const [capital, setCapital] = useState("10000");
  // 2026-09-22：預設 1 → 0.1。1 手在本金 $10,000 下是外匯 10~13 倍、黃金 43 倍（見 leverageInfo）；
  //   0.1 手＝外匯約 1.1~1.3 倍、黃金約 4.3 倍。handleReset 也要一起改。
  const [lotSize, setLotSize] = useState("0.1");
  const [startDate, setStartDate] = useState(getMinDateStr("1d"));
  const [endDate, setEndDate] = useState(getYesterdayStr());
  const [entryCondition, setEntryCondition] = useState([]); // ✅ 改為陣列型態
  const [exitCondition, setExitCondition] = useState([]);   // ✅ 改為陣列型態
  const [strategyDescription, setStrategyDescription] = useState([]); // ✅ 改為陣列型態
  const [strategySummary, setStrategySummary] = useState(null);
  const [backtestJson, setBacktestJson] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [animatedSummary, setAnimatedSummary] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [isThinking, setIsThinking] = useState(false);
  const animatedRef = useRef(null);
  const summaryAreaRef = useRef(null);
  const backtestBtnRef = useRef(null);
  const [kpiDialogOpen, setKpiDialogOpen] = useState(false);
  const [strategyTitle, setStrategyTitle] = useState("");
  const [successOpen, setSuccessOpen] = useState(false);
  const [cursorVisible, setCursorVisible] = useState(true);
  const [formSubmitted, setFormSubmitted] = useState(false);
  // 即時驗證（不等提交）：低於最小值 / 日期反序 → 即時標註框
  const capitalInvalid = capital !== "" && Number(capital) < 100;
  const lotSizeInvalid = lotSize !== "" && Number(lotSize) < 0.01;
  const dateRangeInvalid = !!(startDate && endDate && dayjs(startDate).isAfter(dayjs(endDate)));

  const handleReset = () => {
    setSymbol(symbolOptions[0]?.value || symbol);
    setTimeframe(timeframeOptions[0]?.value || "1d");
    setCapital(10000);
    setLotSize(0.1);
    setOrderType(orderTypeOptions[0]?.value || "LONG");
    setStartDate(getMinDateStr(timeframeOptions[0]?.value || "1d"));
    setEndDate(getYesterdayStr());
    setEntryCondition([]);
    setExitCondition([]);
    setStrategyDescription([]);
    setFormSubmitted(false);

  };
  const [errorMessage, setErrorMessage] = useState("");
  const [emptyBacktestErrorMessage, setEmptyBacktestErrorMessage] = useState("");
  const [usageStatus, setUsageStatus] = useState(null);

  // 🪙 2026-09-18：次數用完的 Coin 提示改放在「按鈕旁邊」。
  //   原本只會彈上方的 Snackbar(errorMessage)，使用者常常沒注意到就以為壞掉了。
  //   刻意與 errorMessage 分開成獨立 state：同一件事同時彈兩個地方反而讓人不確定該點哪個，
  //   所以 ASK_USE_COIN 只走這條、顯示在使用者視線焦點（按鈕下方）。
  //   形狀：{ kind: "preview" | "backtest", message: string }
  const [coinPrompt, setCoinPrompt] = useState(null);

  // 📊 事前提示用：在使用者「按下去之前」就知道還剩幾次、這次會不會花 Coin。
  //   判斷邏輯必須與後端中介層完全一致，否則提示會說謊：
  //     server/middlewares/checkBacktestLimit.js:50-62
  //     server/middlewares/checkStrategyLimit.js:53-65
  //   後端規則：用量 >= 免費上限 → 沒帶 useCoin 就回 ASK_USE_COIN；
  //             帶了 useCoin 但「餘額<=0 或 Coin 次數已達上限」→ LIMIT_EXCEEDED；否則放行扣 1 Coin。
  //   欄位語意見 strategyService.getStrategyUsageStatusService：*Today=已用、*Limit=上限。
  const quotaInfo = useMemo(() => {
    const build = (usedKey, limitKey, coinUsedKey, coinLimitKey) => {
      const used = Number(usageStatus?.[usedKey] ?? 0);
      const limit = Number(usageStatus?.[limitKey] ?? 0);
      const coinUsed = Number(usageStatus?.[coinUsedKey] ?? 0);
      const coinLimit = Number(usageStatus?.[coinLimitKey] ?? 0);
      const balance = Number(usageStatus?.coinWalletBalance ?? 0);
      return {
        freeLeft: Math.max(0, limit - used),
        freeLimit: limit,
        balance,
        coinLeft: Math.max(0, coinLimit - coinUsed),
        // 與後端同一條判斷：餘額 > 0 且當日 Coin 次數未達上限
        coinAvailable: balance > 0 && coinUsed < coinLimit,
      };
    };
    return {
      preview: build("strategyToday", "strategyLimit", "coin_strategyToday", "coin_strategyLimit"),
      backtest: build("backtestToday", "backtestLimit", "coin_backtestToday", "coin_backtestLimit"),
    };
  }, [usageStatus]);

  const EntryCondition_maxLength = 250;
  const ExitCondition_maxLength = 250;
  const StrategyDescription_maxLength = 400;
  const EntryCondition_isOverLimit = entryCondition.length > EntryCondition_maxLength;
  const ExitCondition_isOverLimit = exitCondition.length > ExitCondition_maxLength;
  const StrategyDescription_isOverLimit =
    strategyDescription.length > StrategyDescription_maxLength;
  const [openEntryDialog, setOpenEntryDialog] = useState(false);
  const [openExitDialog, setOpenExitDialog] = useState(false);
  const [openExtraDialog, setOpenExtraDialog] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const timerRef = useRef(null);
  const [run, setRun] = useState(false);
  const [openStrategyGroupDialog, setOpenStrategyGroupDialog] = useState(false);
  const [symbolOptions, setSymbolOptions] = useState([]);
  const [timeframeOptions, setTimeframeOptions] = useState([]);
  const [orderTypeOptions, setOrderTypeOptions] = useState(DEFAULT_DIRECTIONS);
  // 每手名目價值(美元)，由 /strategy/options 附帶回傳（後端 symbolNotionalService）。
  // 算不出來的商品是 null → 不顯示槓桿，絕不猜。
  const [notionalPerLotUsd, setNotionalPerLotUsd] = useState({});
  // EXE 實盤的名目槓桿上限（後端 assistantRiskLimits.MAX_ASSISTANT_LEVERAGE，隨 options 回傳）。拿不到就是 null → 不顯示實盤手數。
  const [maxAssistantLeverage, setMaxAssistantLeverage] = useState(null);

  // 🎚️ 2026-09-21 槓桿提示：預設「本金 $10,000 + 手數 1」對黃金以現價約 43 倍、
  //   多數外匯 10~13 倍。使用者照預設跑「買進持有黃金」→ 2022 回檔就被打爆(-108.93%)，
  //   而同期金價其實漲了 146% —— 回測直接用這個手數，按下去之前就該看得到槓桿。
  //   （EXE 實盤不是用這個手數：登入時會被 assistantRiskLimits 夾到 3 倍內，軟上限。）
  //   只提醒、不阻擋：AI 助理是給有交易基礎的人測自己的想法，高槓桿可能正是他要測的。
  //   以最近一根日線收盤估算＝「現在下單」的槓桿；回測期間的倍數會隨當時價格不同。
  const leverageInfo = useMemo(() => {
    const perLot = Number(notionalPerLotUsd?.[symbol]);
    const lots = Number(lotSize);
    const cap = Number(capital);
    if (!(perLot > 0) || !(lots > 0) || !(cap > 0)) return null;
    const notional = perLot * lots;
    // 2026-09-22：EXE 實盤會把手數夾到 maxAssistantLeverage 倍內（authService → clampAssistantLots）。
    //   公式必須與 server/services/assistantRiskLimits.js 的 maxLotFor 一致：無條件捨去到 0.01、最少 0.01。
    //   名目也是同一個來源（symbolNotionalService），所以這裡算出的就是 EXE 以現價登入時會拿到的手數。
    let liveLots = null;
    const capLev = Number(maxAssistantLeverage);
    if (capLev > 0) {
      const maxLot = Math.max(0.01, Math.floor(((cap * capLev) / perLot) * 100) / 100);
      if (lots > maxLot) liveLots = maxLot;
    }
    return {
      notional,
      leverage: notional / cap,
      liveLots,
      liveLeverage: liveLots !== null ? (liveLots * perLot) / cap : null,
      capLev,
    };
  }, [notionalPerLotUsd, maxAssistantLeverage, symbol, lotSize, capital]);

  // 🛡️ 2026-09-29（A11）：摘要卡的「風控」區塊。AI 摘要固定有「風控：」一行（停損），
  //   舊的 extractStrategyLogic 只取「策略邏輯」之後的文字，風控排在前面被切掉，使用者看不到實際用了哪個停損。
  //   切段規則見 utils/aiSummarySections.js。用 useMemo 從 strategySummary 算，不另存 state：
  //   摘要被作廢（invalidateSummary）或被拒答（strategySummary=null）時自動消失。
  //   ⚠️ 必須放在所有 useState 之後（曾有 TDZ 事故，ESLint 抓不到）。
  const summaryRisk = useMemo(() => getSummaryDisplay(strategySummary).risk, [strategySummary]);

  // 2026-06-13：策略邏輯類輸入一變 → 作廢已生成的摘要/回測，逼使用者重新生成。
  //   修 stale bug：生成摘要後若往上改策略卻沒重生成、直接回測 → 後端會吃到舊摘要(舊策略)。也修「畫面顯示對不上的舊摘要」的困惑。
  const invalidateSummary = () => {
    setStrategySummary(null);
    setAnimatedSummary("");
    setBacktestJson(null);
    // 回測的 Coin 提示是掛在 strategySummary 區塊內的，摘要一作廢它就會連同按鈕一起消失；
    // state 不一起清會留下看不見的殘留值，之後重新生成摘要時又突然冒出來。
    setCoinPrompt(null);
  };
  const strategyDirtyFirstRun = useRef(true);
  useEffect(() => {
    if (strategyDirtyFirstRun.current) { strategyDirtyFirstRun.current = false; return; }
    if (strategySummary) invalidateSummary();
    // ⚠️ orderType 故意不放 deps：它被下方「語系 effect」(setOrderType(LANGUAGE.Main.Content16)) 設定，
    //    放進來會害「切換 App 語言」誤清摘要。改在 orderType 下拉的 onChange 直接作廢。
    //    startDate/endDate 也不放（純回測區間，換區間回測同一策略屬正常操作）。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol, timeframe, capital, lotSize, entryCondition, exitCondition, strategyDescription]);

  useEffect(() => {
    const fetchOptions = async () => {
      if (demoMode) {
        setSymbolOptions([{ value: "EURUSD" }, { value: "GBPUSD" }, { value: "AUDUSD" }, { value: "USDJPY" }, { value: "XAUUSD" }]);
        setTimeframeOptions([{ value: "1h" }, { value: "4h" }, { value: "1d" }]);
        setOrderTypeOptions(DEFAULT_DIRECTIONS);
        return;
      }
      try {
        const res = await axiosInstance.get("/strategy/options");
        const { symbols, timeframes, directions, notionalPerLotUsd: notional } = res.data;

        setSymbolOptions(symbols || []);
        setTimeframeOptions(timeframes || []);
        setNotionalPerLotUsd(notional || {});
        setMaxAssistantLeverage(res.data.maxAssistantLeverage ?? null);
        if (directions && directions.length > 0) {
          setOrderTypeOptions(directions);
        }

        // 自動選取第一個選項（如果目前值不在列表中或是初始狀態）
        if (symbols?.length > 0) {
          const exists = symbols.find(s => s.value === symbol);
          if (!exists || !symbol) setSymbol(symbols[0].value);
        }
        if (timeframes?.length > 0) {
          const exists = timeframes.find(t => t.value === timeframe);
          if (!exists || !timeframe) setTimeframe(timeframes[0].value);
        }
        if (directions?.length > 0) {
          const exists = directions.find(d => d.value === orderType);
          if (!exists || !orderType) setOrderType(directions[0].value);
        }
      } catch (err) {
        console.error("Failed to fetch options:", err);
      }
    };
    fetchOptions();
  }, [orderType, symbol, timeframe]);

  useEffect(() => {
    const minStr = getMinDateStr(timeframe);
    const maxStr = getYesterdayStr();

    if (startDate < minStr) {
      setStartDate(minStr);
    } else if (startDate > maxStr) {
      setStartDate(maxStr);
    }

    if (endDate < minStr) {
      setEndDate(minStr);
    } else if (endDate > maxStr) {
      setEndDate(maxStr);
    }
  }, [timeframe, startDate, endDate]);

  const fetchUsageStatus = useCallback(async () => {
    if (demoMode) return; // DEMO：不打 usage API → usageStatus 維持 null → 配額卡自動隱藏
    try {
      const res = await axiosInstance.get("/strategy/usage-status");
      setUsageStatus(res.data);
    } catch (err) {
      console.error(LANGUAGE.Error.Content1, err);
    }
  }, [LANGUAGE.Error.Content1]);

  const animateTyping = async (text) => {
    setAnimatedSummary("");
    setIsTyping(true);
    let current = "";

    // 過濾掉重複的參數標頭，只顯示「策略邏輯」段（風控另外顯示，見 summaryRisk）。
    //   2026-09-29（A11）：改用 utils/aiSummarySections.js 逐行解析，取代舊的 extractStrategyLogic
    //   （舊的會切掉風控、比對不到簡中「策略逻辑」、還會被內文的「logic 」誤切）。
    //   找不到策略邏輯標題（拒答訊息、DEMO 假摘要、舊格式）→ 退回全文，與舊行為一致。
    const filteredText = getSummaryDisplay(text).logic;

    const blink = setInterval(() => setCursorVisible((v) => !v), 500);

    // 判斷是否為純英文（含標點）
    const isMostlyEnglish = /^[\u0020-\u007F\s.,;:!?'"()\[\]\-_/\\ ]*$/.test(filteredText);

    // 顯示步長與速度控制
    const step = isMostlyEnglish ? 2 : 1;
    const delay = isMostlyEnglish ? 1 : 5;

    for (let i = 0; i < filteredText.length; i += step) {
      current += filteredText.slice(i, i + step);
      setAnimatedSummary(current);
      await new Promise((resolve) => setTimeout(resolve, delay));

      if (animatedRef.current) {
        animatedRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
      }
    }

    clearInterval(blink);
    setCursorVisible(false);
    setIsTyping(false);

    // 完成打字後，捲動到回測按鈕
    setTimeout(scrollToBacktestBtn, 300);
  };

  const scrollToKPI = () => {
    const el = document.getElementById("kpi-chart-anchor");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const scrollToSummary = () => {
    if (summaryAreaRef.current) {
      summaryAreaRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  const scrollToBacktestBtn = () => {
    if (backtestBtnRef.current) {
      backtestBtnRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  const handleInsertStrategyGroup = async (group) => {
    if (demoMode) {
      // 本地策略組已帶 entry/exit/filter，直接解析插入（不打 /strategy/strategy-group/{id}）
      const parse = (str) => {
        if (!str) return [];
        const m = str.match(/\((.*?)\)/g);
        if (m) return m.map((x) => x.slice(1, -1));
        return str.split("\n").map((v) => v.trim()).filter(Boolean);
      };
      setEntryCondition(parse(group.entry));
      setExitCondition(parse(group.exit));
      setStrategyDescription(parse(group.filter));
      return;
    }
    try {
      const res = await axiosInstance.get(`/strategy/strategy-group/${group.id}`);
      const { entry, exit, extra } = res.data;

      const parseLogic = (str) => {
        if (!str) return [];
        // 正則表達式抓取括號內容
        const matches = str.match(/\((.*?)\)/g);
        if (matches) return matches.map(m => m.slice(1, -1));
        return str.split("\n").map(v => v.trim()).filter(v => v);
      };

      setEntryCondition(parseLogic(entry));
      setExitCondition(parseLogic(exit));
      setStrategyDescription(parseLogic(extra));
    } catch (err) {
      toast.error(LANGUAGE.Error.Content19);
    }
  };

  // 標籤化輸入框已抽成 module 層 <TagInput>（保留 chip + 加打字框）


  const handleSubmit = async () => {
    // 轉換為字串以進行送出與長度判定
    const entryStr = entryCondition.map(v => `(${v})`).join("");
    const exitStr = exitCondition.map(v => `(${v})`).join("");
    const extraStr = strategyDescription.map(v => `(${v})`).join("");

    if (
      entryStr.length > EntryCondition_maxLength ||
      exitStr.length > ExitCondition_maxLength ||
      extraStr.length > StrategyDescription_maxLength
    ) {
      setErrorMessage(LANGUAGE.Error.Content2);
      return;
    }
    if (entryCondition.length === 0 || exitCondition.length === 0) {
      setErrorMessage(LANGUAGE.Error.Content3);
      return;
    }

    setIsLoading(true);
    setIsThinking(true);

    // 延遲一段時間等待 DOM 渲染後捲動到摘要區域
    setTimeout(scrollToSummary, 100);

    setBacktestJson(null);
    setStrategySummary(null);
    setAnimatedSummary("");
    setEmptyBacktestErrorMessage("");
    setCoinPrompt(null);   // 重按主按鈕時，舊的 Coin 提示要收掉，不然會殘留在畫面上
    if (demoMode) {
      // DEMO：不接 OpenAI，依輸入欄位組出可讀的假摘要 + 打字動畫
      const entryStrD = entryCondition.map((v) => `(${v})`).join("");
      const exitStrD = exitCondition.map((v) => `(${v})`).join("");
      const fake = (LANGUAGE.Main.DemoSummary ||
        "本策略針對 {symbol}（{timeframe}）以 {orderType} 方向操作。進場條件：{entry}；出場條件：{exit}。系統依歷史資料模擬進出場訊號與部位管理，並評估報酬與風險。（本內容為 DEMO 假資料，非真實 AI 生成）")
        .replace("{symbol}", symbol)
        .replace("{timeframe}", timeframe)
        .replace("{orderType}", orderType)
        .replace("{entry}", entryStrD)
        .replace("{exit}", exitStrD);
      setTimeout(() => {
        setIsThinking(false);
        setStrategySummary(fake);
        animateTyping(fake);
        setIsLoading(false);
      }, 900);
      return;
    }
    try {
      const res = await axiosInstance.post("/strategy/preview", {
        symbol,
        timeframe,
        capital,
        lotSize,
        startDate,
        orderType,
        endDate,
        entryCondition: entryStr,
        exitCondition: exitStr,
        strategyDescription: extraStr,
      });

      setIsThinking(false);
      const isMatch = res.data.summary.includes("輸入格式不符");
      if (isMatch) {
        setStrategySummary(null);
      } else {
        setStrategySummary(res.data.summary);
        trackEvent("generate_strategy", { symbol, timeframe, order_type: orderType });
      }

      animateTyping(res.data.summary);
      await fetchUsageStatus();
    } catch (err) {
      setIsThinking(false);
      const isLimitError =
        err.response?.status === 403 && err.response?.data?.code === "LIMIT_EXCEEDED";
      const isUseCoinError =
        err.response?.status === 403 && err.response?.data?.code === "ASK_USE_COIN";
      const rawMessage = err.response?.data?.message;

      if (isLimitError) {
        setErrorMessage(
          <>
            {rawMessage}
            <a
              href="/coins"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: COLORS.error,
                fontWeight: "bold",
                textDecoration: "underline",
                marginLeft: "6px",
              }}
            >
              {LANGUAGE.Main.Content1}
            </a>
          </>
        );
      }
      if (isUseCoinError) {
        // 改走按鈕旁的提示（coinPrompt），不再彈上方 Snackbar —— 使用者常常沒注意到。
        //   實際重跑的邏輯已抽成 retryPreviewWithCoin()。
        setCoinPrompt({ kind: "preview", message: rawMessage });
      } else if (err.response?.status === 403 && err.response?.data?.code === "") {
        // 真正登入過期情境
        navigate("/authentication/sign-in");
      } else {
        console.error(LANGUAGE.Error.Content6, err);
        setErrorMessage(LANGUAGE.Error.Content7);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // 🪙 用 Coin 重跑「生成摘要」。內容是從原本內嵌在 Snackbar 按鈕裡的 onClick 原封不動搬出來的，
  //   只把 entryStr/exitStr/extraStr 改成就地重算（它們本來取自 handleSubmit 的閉包，
  //   而這三個值是 state 的純函式，重算等價）。搬出來才能給按鈕下方的提示共用。
  const retryPreviewWithCoin = async () => {
    const entryStr = entryCondition.map((v) => `(${v})`).join("");
    const exitStr = exitCondition.map((v) => `(${v})`).join("");
    const extraStr = strategyDescription.map((v) => `(${v})`).join("");
    try {
      setCoinPrompt(null);
      setErrorMessage("");
      setIsThinking(true);
      setTimeout(scrollToSummary, 100);
      const res = await axiosInstance.post("/strategy/preview?useCoin=true", {
        symbol,
        timeframe,
        capital,
        lotSize,
        startDate,
        orderType,
        endDate,
        entryCondition: entryStr,
        exitCondition: exitStr,
        strategyDescription: extraStr,
      });
      setIsThinking(false);
      const isMatch = res.data.summary.includes("輸入格式不符");
      if (isMatch) {
        setStrategySummary(null);
      } else {
        setStrategySummary(res.data.summary);
      }
      animateTyping(res.data.summary);
      await fetchUsageStatus();
    } catch (e) {
      setIsThinking(false);
      console.error(LANGUAGE.Error.Content5, e);
      setErrorMessage(
        <>
          {e.response?.data?.message}
          <a
            href="/coins"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: COLORS.error, fontWeight: "bold", textDecoration: "underline", marginLeft: "6px" }}
          >
            {LANGUAGE.Main.Content1}
          </a>
        </>
      );
    }
  };

  // 🪙 用 Coin 重跑「回測」。走共用的 submitBacktest，錯誤處理與主流程一致。
  const retryBacktestWithCoin = async () => {
    try {
      setCoinPrompt(null);
      await submitBacktest(true);
    } catch (e) {
      console.error(LANGUAGE.Error.Content5, e);
      setKpiDialogOpen(false);
      setErrorMessage(
        <>
          {e.response?.data?.message}
          <a
            href="/coins"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: COLORS.error, fontWeight: "bold", textDecoration: "underline", marginLeft: "6px" }}
          >
            {LANGUAGE.Main.Content1}
          </a>
        </>
      );
    } finally {
      setIsLoading(false);
    }
  };

  // 🔁 2026-09-01：主流程與「用 MAS Coin 續跑」共用同一份送出邏輯。
  //   原本兩邊各寫一次，Coin 那條漏掉 setIsLoading / setKpiDialogOpen / isDataEmpty 處理 ——
  //   使用者按下去畫面完全沒反應（沒有 loading），過一陣子才突然跳出績效。
  //   抽成一份就不會再各自漂移。呼叫端只負責 catch 與 finally 的 setIsLoading(false)。
  const submitBacktest = async (useCoin = false) => {
    setIsLoading(true);
    setKpiDialogOpen(true);
    setBacktestJson(null);
    setEmptyBacktestErrorMessage("");
    setErrorMessage("");
    setCoinPrompt(null);   // 重按主按鈕時，舊的 Coin 提示要收掉，不然會殘留在畫面上
    const res = await axiosInstance.post(
      useCoin ? "/strategy/backtest?useCoin=true" : "/strategy/backtest",
        { strategySummary, timeframe, startDate, endDate, symbol, capital, volume: lotSize,
                    orderType,
                    entryCondition: entryCondition.map((v) => `(${v})`).join(""),
                    exitCondition: exitCondition.map((v) => `(${v})`).join(""),
                    strategyDescription: strategyDescription.map((v) => `(${v})`).join("") },
      { timeout: 300000 }
    );
    // ✅ 檢查是否有進場訊號資料
    if (res.data?.isDataEmpty) {
      // 無進出場訊號：關掉報告 modal；只用頁面下方 Alert，不再彈上方 toast（避免重複）
      setKpiDialogOpen(false);
      setEmptyBacktestErrorMessage(LANGUAGE.Error.Content9);
      setBacktestJson(null);
    } else {
      setBacktestJson(res?.data || res);
      trackEvent("run_backtest", { timeframe });
    }
    await fetchUsageStatus();
    return res;
  };

  const handleBacktest = async () => {
    if (demoMode) {
      // DEMO：模擬回測幾秒 → 載入本地假 KPI
      setIsLoading(true);
      setKpiDialogOpen(true);
      setBacktestJson(null);
      setEmptyBacktestErrorMessage("");
      // 2026-09-29（A10 審查 F3）：fakeKPI 的餘額是以本金 10,000 做的。「期末已平倉餘額」圖會從報表反推初始本金並寫在圖例，
      //   使用者把本金改成 50,000 時若不縮放，摘要卡寫 50,000、圖例卻寫 10,000。依表單本金等比例縮放餘額
      //   （百分比類欄位與本金無關，不用動）。本金一改摘要就作廢（invalidateSummary），所以這裡的 capital 就是摘要卡上的本金。
      const demoCapital = Number(capital);
      const demoKpi = rescaleBalances(demoFakeKPI, demoCapital / DEMO_FAKE_KPI_CAPITAL);
      setTimeout(() => {
        setBacktestJson(demoKpi);
        setIsLoading(false);
      }, 2500);
      return;
    }
    try {
      await submitBacktest(false);
    } catch (err) {
      // 任何回測失敗（無訊號/執行失敗/額度…）→ 關掉報告 modal，讓頁面下方錯誤提示可見
      setKpiDialogOpen(false);
      const isLimitError =
        err.response?.status === 403 && err.response?.data?.code === "LIMIT_EXCEEDED";
      const isUseCoinError =
        err.response?.status === 403 && err.response?.data?.code === "ASK_USE_COIN";
      const rawMessage = err.response?.data?.message;
      if (isLimitError) {
        setErrorMessage(
          <>
            {rawMessage}
            <a
              href="/coins"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: COLORS.error,
                fontWeight: "bold",
                textDecoration: "underline",
                marginLeft: "6px",
              }}
            >
              {LANGUAGE.Main.Content1}
            </a>
          </>
        );
        setIsLoading(false);
      }
      if (isUseCoinError) {
        // 改走按鈕旁的提示（coinPrompt），不再彈上方 Snackbar —— 使用者常常沒注意到。
        //   實際重跑的邏輯已抽成 retryBacktestWithCoin()。
        setCoinPrompt({ kind: "backtest", message: rawMessage });
      } else if (err.response?.status === 403 && err.response?.data?.code === "") {
        // 真正登入過期情境
        navigate("/authentication/sign-in");
      } else {
        // 後端把回測流程的各種失敗正規化成 code（見 strategyController.classifyBacktestError），
        // 這裡據 code 對應到使用者看得懂的文案；未列入的才走最終「未知錯誤」fallback。
        const beMsg = err.response?.data?.message;
        const codeTextMap = {
          backtest_error: LANGUAGE.Error.Content8,        // 回測執行失敗，無需扣次數
          backtest_null: LANGUAGE.Error.Content9,         // 無買賣訊號（需扣次數）
          balance_insufficient: LANGUAGE.Error.Content21, // AI 服務額度不足
          service_unavailable: LANGUAGE.Error.Content22,  // AI 服務忙線
          generate_failed: LANGUAGE.Error.Content23,      // 策略生成失敗
          // 防禦性：19 個週期選項都已支援，正常操作不會走到；沿用「回測執行失敗」文案，
          // 精準原因（UNSUPPORTED_TIMEFRAME:xxx）在回應的 detail 與伺服器日誌裡。
          unsupported_timeframe: LANGUAGE.Error.Content8,
          // F1：回看超過該週期上限（精準原因在回應的 detail 與伺服器日誌）；沿用「回測執行失敗」文案
          unsupported_lookback: LANGUAGE.Error.Content8,
        };
        const mappedText = codeTextMap[beMsg];
        if (mappedText) {
          setEmptyBacktestErrorMessage(mappedText);
        } else if (!isLimitError) {
          // 真正未分類的錯誤 → 通用「未知錯誤」提示；排除 isLimitError（前面已設含「前往 Coins」連結的訊息，不可覆蓋）
          // 只用頁面下方 Alert，不再彈上方 toast（避免重複）
          setEmptyBacktestErrorMessage(LANGUAGE.Error.Content20);
        }
        await fetchUsageStatus();
        console.error(LANGUAGE.Error.Content20, err);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveStrategy = async () => {
    if (demoMode) { window.open("https://trade.masquant.com/authentication/sign-in", "_blank"); return; }
    let count = usageStatus.strategySaveLimit - usageStatus.currentStrategyCount;
    setIsLoading(true);
    if (count === 0) {
      setErrorMessage(
        <>
          {LANGUAGE.Error.Content11}
          <a
            href="/plans"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: COLORS.error,
              fontWeight: "bold",
              textDecoration: "underline",
              marginLeft: "6px",
            }}
          >
            {LANGUAGE.Error.Content12}
          </a>
        </>
      );
      setIsLoading(false);
    } else {
      try {
        await axiosInstance.post("/strategy/save", {
          title: strategyTitle,
          symbol,
          timeframe,
          capital,
          lotSize,
          startDate,
          orderType,
          endDate,
          entryCondition: entryCondition.map(v => `(${v})`).join(""),
          exitCondition: exitCondition.map(v => `(${v})`).join(""),
          strategyDescription: strategyDescription.map(v => `(${v})`).join(""),
          summaryText: strategySummary,
          reportJson: backtestJson,
        });
        setSuccessOpen(true);
        navigate("/strategy-manager");
      } catch (err) {
        console.error(LANGUAGE.Error.Content13, err);
        toast.error(LANGUAGE.Error.Content14);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const baseSteps = [
    {
      target: "h4", // 頁面標題「🧠 策略摘要產生器」
      content: LANGUAGE.Guided.Content1,
      placement: "top",
      disableBeacon: true,
      spotlightPadding: 10,
    },
    {
      target: ".strategy-Symbol",
      content: LANGUAGE.Guided.Content2,
      placement: "bottom-end",
      disableBeacon: true,
      spotlightPadding: 8,
    },
    {
      target: ".strategy-timeframe",
      content: LANGUAGE.Guided.Content3,
      placement: "bottom-end",
      disableBeacon: true,
      spotlightPadding: 8,
    },
    {
      target: ".strategy-capital",
      content: LANGUAGE.Guided.Content4,
      placement: "bottom-end",
      disableBeacon: true,
      spotlightPadding: 8,
    },
    {
      target: ".strategy-lotSize",
      content: LANGUAGE.Guided.Content5,
      placement: "bottom-end",
      disableBeacon: true,
      spotlightPadding: 8,
    },
    {
      target: ".strategy-startYear",
      content: LANGUAGE.Guided.Content6,
      placement: "bottom",
      disableBeacon: true,
      spotlightPadding: 8,
    },
    {
      target: ".strategy-endYear",
      content: LANGUAGE.Guided.Content7,
      placement: "bottom",
      disableBeacon: true,
      spotlightPadding: 8,
    },
    {
      target: ".strategy-orderType",
      content: LANGUAGE.Guided.Content8,
      placement: "bottom",
      disableBeacon: true,
      spotlightPadding: 8,
    },
    {
      target: ".insert-strategy-group-btn",
      content: LANGUAGE.Guided.Content19,
      placement: "bottom",
      disableBeacon: true,
      spotlightPadding: 8,
    },
    {
      target: ".strategy-entryCondition",
      content: LANGUAGE.Guided.Content9,
      placement: "top",
      disableBeacon: true,
      spotlightPadding: 8,
    },
    {
      target: ".strategy-exitCondition",
      content: LANGUAGE.Guided.Content10,
      placement: "top",
      disableBeacon: true,
      spotlightPadding: 8,
    },
    {
      target: ".strategy-description",
      content: LANGUAGE.Guided.Content11,
      placement: "top",
      disableBeacon: true,
      spotlightPadding: 8,
    },
    {
      target: ".generate-summary-btn",
      content: LANGUAGE.Guided.Content12,
      placement: "top",
      disableBeacon: true,
      spotlightPadding: 8,
    },
    // {
    //   target: ".backtest-button",
    //   content: "策略產生後可點擊這裡進行回測，查看歷史績效與報表。",
    //   placement: "top",
    //   disableBeacon: true,
    //   spotlightPadding: 8,
    // },
    // {
    //   target: ".performance-area",
    //   content: "這裡將顯示策略的 KPI 與視覺化績效圖表。",
    //   placement: "top",
    //   disableBeacon: true,
    //   spotlightPadding: 8,
    // },
    {
      // 2026-05-25 操作教學按鈕在固定 navbar 頂端，placement 改 bottom-end
      // 讓 tooltip 落到 navbar 下方（原 left 會垂直置中對齊按鈕 → 頂出畫面被切）
      target: ".help-tour-btn",
      content: LANGUAGE.Guided.Content13,
      placement: "bottom-end",
      disableBeacon: true,
      spotlightPadding: 8,
    },
    {
      target: "body",
      content: LANGUAGE.Guided.Content14,
      placement: "center",
      disableBeacon: true,
      spotlightPadding: 10,
    },
  ];

  // DEMO 頁沒有 navbar 的 .help-tour-btn，濾掉該步避免 Joyride 找不到目標
  const steps = demoMode ? baseSteps.filter((s) => s.target !== ".help-tour-btn") : [...baseSteps];

  useEffect(() => {
    fetchUsageStatus();
  }, [fetchUsageStatus]);

  // ✅ 第一次登入才啟動導覽
  useEffect(() => {
    if (demoMode) return; // DEMO 導覽改由下方專屬 effect(只跑一次)負責，避免 summary/backtest 變化時重複彈出
    const params = new URLSearchParams(window.location.search);
    const skipDemo = params.get("skip_demo") || params.get("skip_intro");

    const firstLogin = localStorage.getItem("firstLogin");
    const hasSeenTour = localStorage.getItem("hasSeenStrategyTour");

    // 如果帶有 skip_demo 參數，直接標記為已看過
    if (skipDemo) {
      localStorage.setItem("hasSeenStrategyTour", "true");
      return;
    }

    // 只要不是 "true" 就視為沒看過 (包含 null, "false", undefined)
    if (firstLogin === "true" && hasSeenTour !== "true") {
      // console.log("🚀 啟動導覽!");
      setTimeout(() => setRun(true), 500); // 稍微延遲以確保動畫完成
    }
  }, [steps.length, strategySummary, backtestJson, LANGUAGE]);

  // DEMO：進頁自動啟動導覽「一次」。用 ref 鎖定，且 deps 不含 strategySummary/backtestJson，
  //   所以按「生成摘要 / 回測」時不會再重複彈出導覽。
  const demoTourStarted = useRef(false);
  useEffect(() => {
    if (demoMode && !demoTourStarted.current && LANGUAGE?.Guided?.Content1) {
      demoTourStarted.current = true;
      const t = setTimeout(() => setRun(true), 800);
      return () => clearTimeout(t);
    }
  }, [demoMode, LANGUAGE]);

  useEffect(() => {
    const restart = () => setRun(true);
    window.addEventListener("restartTour", restart);
    return () => window.removeEventListener("restartTour", restart);
  }, []);

  useEffect(() => {
    if (LANGUAGE?.Main?.Content16) {
      setOrderType(LANGUAGE.Main.Content16);
    }
  }, [LANGUAGE?.Main?.Content16]);

  useEffect(() => {
    if (isLoading) {
      setElapsedTime(0);
      timerRef.current = setInterval(() => {
        setElapsedTime((prev) => prev + 1);
      }, 1000);
    } else {
      clearInterval(timerRef.current);
    }

    return () => clearInterval(timerRef.current);
  }, [isLoading]);

  const useDotAnimation = (interval = 500) => {
    const [dots, setDots] = useState(".");

    useEffect(() => {
      const timer = setInterval(() => {
        setDots((prev) => (prev.length >= 3 ? "." : prev + "."));
      }, interval);
      return () => clearInterval(timer);
    }, [interval]);

    return dots;
  };

  const backtestStageText = (sec, dots) => {
    if (sec <= 10) return `${LANGUAGE?.Main?.BacktestStage1}${dots}`;
    if (sec <= 20) return `${LANGUAGE?.Main?.BacktestStage2}${dots}`;
    if (sec <= 30) return `${LANGUAGE?.Main?.BacktestStage3}${dots}`;
    return `${LANGUAGE?.Main?.BacktestStage4}${dots}`;
  };

  const dots = useDotAnimation(); // 每 500ms 更新一次「.」動畫
  const stageText = backtestStageText(elapsedTime, dots);

  // 🪙 按鈕下方的「次數 / Coin」區塊。兩個按鈕（生成摘要、回測）共用同一份。
  //   上半：事前提示 —— 使用者按下去之前就知道還剩幾次、這次會不會花 Coin。
  //   下半：真的撞到上限時，把「使用 MAS Coin 繼續 / 取消」直接放在這裡（視線焦點），
  //         不再只依賴上方那個容易被忽略的 Snackbar。
  const renderQuotaArea = (kind, onUseCoin) => {
    const q = quotaInfo[kind];
    const prompt = coinPrompt?.kind === kind ? coinPrompt : null;
    // usageStatus 還沒載回來就先不顯示，避免閃一下「剩 0 次」嚇到人
    if (!usageStatus && !prompt) return null;

    // 依緊急度給不同的顏色與字重。COLORS.grayMedian(#C4C4C4) 在白底只有 1.74:1，
    // 而這行提示的存在目的就是「要被看見」，所以不用它。
    // token 內白底對比：grayDark 3.45:1、red 4.67:1(達小字 AA)、main 2.99:1。
    const hintStyle = !usageStatus
      ? {}
      : q.freeLeft > 0
        ? { color: COLORS.grayDark, fontWeight: "regular" }        // 一般資訊
        : q.coinAvailable
          ? { color: COLORS.red, fontWeight: "bold" }              // 這次會花錢 → 要醒目
          : { color: COLORS.red, fontWeight: "bold" };             // 已用完 → 同樣醒目

    const hint = !usageStatus ? null : q.freeLeft > 0 ? (
      <>
        {LANGUAGE.Main.QuotaFreeLeft}
        <Box component="span" sx={{ fontWeight: "bold", color: COLORS.main, mx: 0.5 }}>
          {q.freeLeft} / {q.freeLimit}
        </Box>
      </>
    ) : q.coinAvailable ? (
      <>
        {LANGUAGE.Main.QuotaWillUseCoin}
        {/* 不用寫死括號：全形括號在英文介面會變成 CJK 標點。用中點分隔，四個語系都自然 */}
        <Box component="span" sx={{ ml: 0.75, opacity: 0.85 }}>
          · {LANGUAGE.Main.QuotaCoinBalance} {q.balance}
        </Box>
      </>
    ) : (
      <>
        {LANGUAGE.Main.QuotaAllUsed}
        {/* className 必要：globals.js:62 對所有 a 設了 text-decoration:none !important，
            只有 .auth-underline 這個 class 能覆寫回底線 */}
        <a
          className="auth-underline"
          href="/coins"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: COLORS.error, fontWeight: "bold", marginLeft: "6px" }}
        >
          {LANGUAGE.Main.Content1}
        </a>
      </>
    );

    return (
      <MDBox mt={prompt ? 2 : 1.25}>
        {/* 提示框出現時就不再重複顯示上面那行（兩者會講同一件事） */}
        {hint && !prompt && (
          <MDTypography
            variant="caption"
            sx={{ display: "block", textAlign: "center", lineHeight: 1.7, fontSize: "0.82rem", ...hintStyle }}
          >
            {hint}
          </MDTypography>
        )}

        {prompt && (
          <Alert
            severity="warning"
            sx={{
              mt: 1.25,
              width: "100%",
              bgcolor: "rgba(255, 152, 0, 0.1)",
              color: COLORS.main,
              fontWeight: "medium",
              "& .MuiAlert-message": { width: "100%", lineHeight: 1.8 },
            }}
          >
            {/* ⚠️ 用前端的文案，不要用後端回傳的 prompt.message ——
                中介層(checkBacktestLimit.js:54 / checkStrategyLimit.js:57)的訊息寫死中文、
                而且整個中介層沒有語系概念，英文介面會冒出中文。
                後端訊息只留作 i18n key 缺漏時的保底。 */}
            <Box sx={{ mb: 1.25 }}>
              {(kind === "backtest"
                ? LANGUAGE.Main.QuotaAskUseCoinBacktest
                : LANGUAGE.Main.QuotaAskUseCoinPreview) || prompt.message}
            </Box>
            <Box display="flex" gap={1} flexWrap="wrap">
              {/* 後端是在「檢查 Coin 夠不夠之前」就先回 ASK_USE_COIN，所以餘額不足的人
                  也會走到這裡。這種情況給「使用 Coin」按鈕等於給一顆必定失敗的按鈕，
                  改成直接導去購買。 */}
              {q.coinAvailable ? (
                <MDButton variant="gradient" color="warning" size="small" onClick={onUseCoin}>
                  {LANGUAGE.Main.Content2}
                </MDButton>
              ) : (
                <MDButton
                  variant="gradient"
                  color="warning"
                  size="small"
                  component="a"
                  href="/coins"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {LANGUAGE.Main.Content1}
                </MDButton>
              )}
              <MDButton variant="outlined" color="secondary" size="small" onClick={() => setCoinPrompt(null)}>
                {LANGUAGE.Main.Content3}
              </MDButton>
            </Box>
          </Alert>
        )}
      </MDBox>
    );
  };

  return (
    <>
      <GlobalStyles
        styles={{
          "@keyframes fadeIn": {
            from: { opacity: 0, transform: "translateY(10px)" },
            to: { opacity: 1, transform: "translateY(0)" },
          },
          ".joyride-fadeIn": {
            animation: "fadeIn 0.3s ease-in-out",
          },
        }}
      />
      <Joyride
        steps={steps}
        run={run}
        continuous
        showSkipButton
        showProgress
        scrollToFirstStep
        disableScrolling={false}
        // 2026-05-25 修導覽超出畫面：scrollOffset 留 navbar(fixed 75px) 的空間，
        // 讓每步目標捲到 navbar 下方，避免 spotlight 被蓋住 / tooltip 文字被切
        scrollOffset={120}
        scrollDuration={350}
        locale={{
          next: LANGUAGE.Guided.Content15,
          back: LANGUAGE.Guided.Content16,
          last: LANGUAGE.Guided.Content17,
          skip: LANGUAGE.Guided.Content18,
        }}

        styles={{
          options: {
            zIndex: 10000,
            primaryColor: "#00bcd4",
            backgroundColor: COLORS.white,
            textColor: COLORS.black,
            arrowColor: COLORS.white,
            borderRadius: 16,
            boxShadow: "0 6px 20px rgba(0, 188, 212, 0.25)",
          },
          tooltipContainer: {
            padding: "24px 28px",
            maxWidth: "440px",
            animation: "fadeIn 0.3s ease-in-out",
            backgroundImage: "linear-gradient(135deg, #e0f7fa 0%, #ffffff 100%)",
          },
          buttonNext: {
            backgroundColor: "#00bcd4",
            borderRadius: 8,
            padding: "8px 20px",
            fontWeight: "bold",
          },
          buttonBack: {
            color: COLORS.grayDark,
            fontWeight: "bold",
          },
        }}
        floaterProps={{
          disableAnimation: false,
          styles: {
            floater: { zIndex: 10000 },
          },
        }}
        callback={(data) => {
          const { status } = data;
          if (["finished", "skipped"].includes(status)) {
            localStorage.setItem("hasSeenStrategyTour", "true");
            setRun(false);
          }
        }}
      />

      <Fade in timeout={500}>
        <MDBox p={3} sx={{ background: COLORS.pageBg, minHeight: "100vh" }}>
          {usageStatus && (
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={6} lg={4}>
                <StatusCard
                  title={LANGUAGE.Main.Stat1}
                  count={Math.max(0, usageStatus.strategyLimit - usageStatus.strategyToday)}
                  total={usageStatus.strategyLimit}
                  icon={strategyIcon}
                  remainingLabel={LANGUAGE.Main.Remaining}
                />
              </Grid>
              <Grid item xs={12} sm={6} lg={4}>
                <StatusCard
                  title={LANGUAGE.Main.Stat2}
                  count={Math.max(0, usageStatus.backtestLimit - usageStatus.backtestToday)}
                  total={usageStatus.backtestLimit}
                  icon={graphIcon}
                  remainingLabel={LANGUAGE.Main.Remaining}
                />
              </Grid>
              <Grid item xs={12} sm={6} lg={4}>
                <StatusCard
                  title={LANGUAGE.Main.Stat3}
                  count={Math.max(0, usageStatus.strategySaveLimit - usageStatus.currentStrategyCount)}
                  total={usageStatus.strategySaveLimit}
                  icon={downloadIcon}
                  remainingLabel={LANGUAGE.Main.Remaining}
                />
              </Grid>
              <Grid item xs={12} sm={6} lg={4}>
                <StatusCard
                  title={LANGUAGE.Main.Stat4}
                  count={usageStatus.coinWalletBalance ?? 0}
                  unit="Coin"
                  icon={mascoinIcon}
                  remainingLabel={LANGUAGE.Main.Remaining}
                />
              </Grid>
              <Grid item xs={12} sm={6} lg={4}>
                <StatusCard
                  title={LANGUAGE.Main.Stat5}
                  count={usageStatus.coin_strategyLimit - usageStatus.coin_strategyToday}
                  total={usageStatus.coin_strategyLimit}
                  icon={coinstrategyIcon}
                  remainingLabel={LANGUAGE.Main.Remaining}
                />
              </Grid>
              <Grid item xs={12} sm={6} lg={4}>
                <StatusCard
                  title={LANGUAGE.Main.Stat6}
                  count={usageStatus.coin_backtestLimit - usageStatus.coin_backtestToday}
                  total={usageStatus.coin_backtestLimit}
                  icon={coingraphIcon}
                  remainingLabel={LANGUAGE.Main.Remaining}
                />
              </Grid>
            </Grid>
          )}

          {/* 2026-05-25 中段標題：AI 策略助理 自己的獨立白卡（跟 AI 智能分析同款外框） */}
          <Card
            sx={{
              p: 4,
              mb: 3,
              borderRadius: 3,
              boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
              textAlign: "center",
            }}
          >
            <MDTypography
              sx={{
                color: COLORS.black,
                fontSize: "28px",
                fontWeight: 700,
                lineHeight: 1.4,
                mb: 0.5,
              }}
            >
              {LANGUAGE.Main.PageTitle || "AI 策略助理"}
            </MDTypography>
            <MDTypography
              sx={{
                color: COLORS.grayDark,
                fontSize: "14px",
                fontWeight: 400,
              }}
            >
              {LANGUAGE.Main.PageSubtitle ||
                "輸入交易邏輯與趨勢等參數，生成您的專屬交易策略"}
            </MDTypography>
          </Card>

          <Card
            sx={{
              p: 3,
              pb: 3,
              borderRadius: 3,
              // 2026-05-25 還原自己的 shadow（外面沒大白卡了，每個 sub 各自獨立）
              boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
              backgroundColor: "white",
              animation: "fadeIn 0.6s ease-out",
            }}
          >
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Box display="flex" alignItems="center">
                <MDTypography sx={{ color: COLORS.black, fontSize: "24px", fontWeight: 700, lineHeight: "normal" }}>
                  {LANGUAGE.Main.SecAi || "AI 智能分析"}
                </MDTypography>
                <Box
                  sx={{
                    ml: 1.5,
                    width: 22,
                    height: 22,
                    borderRadius: "50%",
                    bgcolor: COLORS.yellow,
                    color: COLORS.white,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    fontSize: "14px",
                    fontWeight: "bold",
                    cursor: "pointer"
                  }}
                >
                  i
                </Box>
              </Box>
              <MDButton
                variant="outlined"
                onClick={handleReset}
                sx={{
                  borderRadius: "8px",
                  borderColor: COLORS.main,
                  color: COLORS.main,
                  px: 2,
                  py: 0.5,
                  minHeight: "auto",
                  fontSize: "0.85rem",
                  "&:hover": {
                    borderColor: COLORS.main,
                    bgcolor: "rgba(248, 142, 43, 0.05)"
                  }
                }}
              >
                {LANGUAGE.Main.Reset}
              </MDButton>
            </Box>

            <Grid container spacing={4}>
              {/* Left Column: Parameters */}
              <Grid item xs={12} md={5}>
                {/* Backtest Interval Section */}
                <Box mb={3}>
                  <SectionHeader title={LANGUAGE.Main.SecBacktest || "回測區間"} hint={LANGUAGE.Main.SecBacktestHint || "(最多 5 年前至昨日)"} mb={1.5} />
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <MDTypography sx={{ color: COLORS.black, fontSize: "14px", fontWeight: 400, mb: 0.5, display: "block" }}>
                          {LANGUAGE.Main.Content14}*
                        </MDTypography>
                        <DatePicker
                          className="strategy-startYear"
                          value={dayjs(startDate)}
                          minDate={dayjs(getMinDateStr(timeframe))}
                          maxDate={dayjs(getYesterdayStr())}
                          onChange={(newVal) => {
                            if (newVal && newVal.isValid()) {
                              const str = newVal.format("YYYY-MM-DD");
                              const minStr = getMinDateStr(timeframe);
                              if (str < minStr) {
                                toast.warning(timeframe === '1d' ? LANGUAGE.Error.Content17 : LANGUAGE.Error.Content18);
                                setStartDate(minStr);
                              } else {
                                setStartDate(str);
                              }
                            }
                          }}
                          format="YYYY/MM/DD"
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              sx: {
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: "10px",
                                  bgcolor: COLORS.grayLightest,
                                  border: (formSubmitted && !startDate) || dateRangeInvalid ? "1px solid #f88e2b" : "none",
                                  minHeight: "48px",
                                  "& fieldset": { border: "none" },
                                  "& input": { py: 0, px: 1.5, fontSize: "0.9rem", height: "48px", boxSizing: "border-box" },
                                },
                              },
                            },
                            day: {
                              sx: {
                                "&.Mui-selected": {
                                  bgcolor: "#f88e2b !important",
                                  "&:hover": { bgcolor: "#d35400 !important" },
                                },
                                "&:hover": { bgcolor: "rgba(248, 142, 43, 0.1)" },
                              },
                            },
                            desktopPaper: {
                              sx: {
                                borderRadius: "16px",
                                boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
                              },
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <MDTypography sx={{ color: COLORS.black, fontSize: "14px", fontWeight: 400, mb: 0.5, display: "block" }}>
                          {LANGUAGE.Main.Content18}*
                        </MDTypography>
                        <DatePicker
                          className="strategy-endYear"
                          value={dayjs(endDate)}
                          minDate={dayjs(getMinDateStr(timeframe))}
                          maxDate={dayjs(getYesterdayStr())}
                          onChange={(newVal) => {
                            if (newVal && newVal.isValid()) {
                              const str = newVal.format("YYYY-MM-DD");
                              const maxStr = getYesterdayStr();
                              if (str > maxStr) {
                                setEndDate(maxStr);
                              } else {
                                setEndDate(str);
                              }
                            }
                          }}
                          format="YYYY/MM/DD"
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              sx: {
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: "10px",
                                  bgcolor: COLORS.grayLightest,
                                  border: (formSubmitted && !endDate) || dateRangeInvalid ? "1px solid #f88e2b" : "none",
                                  minHeight: "48px",
                                  "& fieldset": { border: "none" },
                                  "& input": { py: 0, px: 1.5, fontSize: "0.9rem", height: "48px", boxSizing: "border-box" },
                                },
                              },
                            },
                            day: {
                              sx: {
                                "&.Mui-selected": {
                                  bgcolor: "#f88e2b !important",
                                  "&:hover": { bgcolor: "#d35400 !important" },
                                },
                                "&:hover": { bgcolor: "rgba(248, 142, 43, 0.1)" },
                              },
                            },
                            desktopPaper: {
                              sx: {
                                borderRadius: "16px",
                                boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
                              },
                            },
                          }}
                        />
                      </Grid>
                    </Grid>
                  </LocalizationProvider>
                </Box>

                {/* 起始日 > 結束日 → 即時紅字提示（日期區下方、交易設定上面）*/}
                {dateRangeInvalid && (
                  <MDTypography sx={{ color: COLORS.error, fontSize: "12px", fontWeight: 500, mb: 1.5, display: "block" }}>
                    {LANGUAGE.Error.DateRangeInvalid || "回測起始日期不可以大於回測結束日期"}
                  </MDTypography>
                )}

                {/* Trading Settings Section */}
                <Box>
                  <SectionHeader title={LANGUAGE.Main.SecTrade || "交易設定"} mb={1.5} />
                  <Box mb={2}>
                    <MDTypography sx={{ color: COLORS.black, fontSize: "14px", fontWeight: 400, mb: 0.5, display: "block" }}>
                      {LANGUAGE.Main.Content11}*
                    </MDTypography>
                    <TextField
                      fullWidth
                      select
                      className="strategy-Symbol"
                      value={symbol}
                      onChange={(e) => setSymbol(e.target.value)}
                      InputProps={{
                        sx: {
                          borderRadius: "10px",
                          bgcolor: COLORS.grayLightest,
                          border: formSubmitted && !symbol ? "1px solid #f88e2b" : "none",
                          "& fieldset": { border: "none" },
                          minHeight: "48px",
                          "& .MuiSelect-select": { py: 0, px: 1.5, fontSize: "0.9rem", height: "48px", display: "flex", alignItems: "center" },
                        }
                      }}
                    >
                      {symbolOptions.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>{opt.value}</MenuItem>
                      ))}
                    </TextField>
                  </Box>
                  <Grid container spacing={2} mb={2}>
                    <Grid item xs={6}>
                      <MDTypography sx={{ color: COLORS.black, fontSize: "14px", fontWeight: 400, mb: 0.5, display: "block" }}>
                        {LANGUAGE.Main.Content13}*
                        <span style={{ color: COLORS.main, fontSize: "13px", fontWeight: 400, marginLeft: "6px" }}>{LANGUAGE.Error.MinCapitalHint || "（最低 100）"}</span>
                      </MDTypography>
                      <TextField
                        fullWidth
                        type="number"
                        className="strategy-capital"
                        value={capital}
                        onChange={(e) => setCapital(e.target.value)}
                        inputProps={{ min: 100 }}
                        InputProps={{
                          sx: {
                            borderRadius: "10px",
                            bgcolor: COLORS.grayLightest,
                            border: (formSubmitted && !capital) || capitalInvalid ? "1px solid #f88e2b" : "none",
                            "& fieldset": { border: "none" },
                            minHeight: "48px",
                            "& input": { py: 0, px: 1.5, fontSize: "0.9rem", height: "48px", boxSizing: "border-box" },
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <MDTypography sx={{ color: COLORS.black, fontSize: "14px", fontWeight: 400, mb: 0.5, display: "block" }}>
                        {LANGUAGE.Main.Content12}*
                      </MDTypography>
                      <TextField
                        fullWidth
                        select
                        className="strategy-timeframe"
                        value={timeframe}
                        onChange={(e) => setTimeframe(e.target.value)}
                        InputProps={{
                          sx: {
                            borderRadius: "10px",
                            bgcolor: COLORS.grayLightest,
                            border: formSubmitted && !timeframe ? "1px solid #f88e2b" : "none",
                            "& fieldset": { border: "none" },
                            minHeight: "48px",
                            "& .MuiSelect-select": { py: 0, px: 1.5, fontSize: "0.9rem", height: "48px", display: "flex", alignItems: "center" },
                          }
                        }}
                      >
                        {timeframeOptions.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>{opt.value}</MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                  </Grid>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <MDTypography sx={{ color: COLORS.black, fontSize: "14px", fontWeight: 400, mb: 0.5, display: "block" }}>
                        {LANGUAGE.Main.Content37}*
                        <span style={{ color: COLORS.main, fontSize: "13px", fontWeight: 400, marginLeft: "6px" }}>{LANGUAGE.Error.MinLotHint || "（最低 0.01）"}</span>
                      </MDTypography>
                      <TextField
                        fullWidth
                        type="number"
                        className="strategy-lotSize"
                        value={lotSize}
                        onChange={(e) => setLotSize(e.target.value)}
                        inputProps={{ min: 0.01, step: 0.01 }}
                        InputProps={{
                          sx: {
                            borderRadius: "10px",
                            bgcolor: COLORS.grayLightest,
                            border: (formSubmitted && !lotSize) || lotSizeInvalid ? "1px solid #f88e2b" : "none",
                            "& fieldset": { border: "none" },
                            minHeight: "48px",
                            "& input": { py: 0, px: 1.5, fontSize: "0.9rem", height: "48px", boxSizing: "border-box" },
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <MDTypography sx={{ color: COLORS.black, fontSize: "14px", fontWeight: 400, mb: 0.5, display: "block" }}>
                        {LANGUAGE.Main.Content15}*
                      </MDTypography>
                      <TextField
                        fullWidth
                        select
                        className="strategy-orderType"
                        value={orderType}
                        onChange={(e) => { setOrderType(e.target.value); invalidateSummary(); }}
                        InputProps={{
                          sx: {
                            borderRadius: "10px",
                            bgcolor: COLORS.grayLightest,
                            border: formSubmitted && !orderType ? "1px solid #f88e2b" : "none",
                            "& fieldset": { border: "none" },
                            minHeight: "48px",
                            "& .MuiSelect-select": { py: 1.5, px: 1.5, fontSize: "0.9rem" },
                          }
                        }}
                      >
                        {orderTypeOptions.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.value === "LONG"
                              ? (LANGUAGE.Main.Content16 || "Long")
                              : opt.value === "SHORT"
                                ? (LANGUAGE.Main.Content17 || "Short")
                                : (LANGUAGE.Main.Content41 || "Both")}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                  </Grid>

                  {/* 🎚️ 槓桿提示（見 leverageInfo 的說明）。放在「手數/下單類型」這排下面的整列，
                      不塞進半寬欄位，否則兩欄高度會不齊。≥10 倍轉紅字並給出量化的爆倉距離。
                      用 ≥ 而不是 >：USDCAD/USDCHF 在預設值下剛好 10.0 倍，不該因為邊界被當成安全。 */}
                  {leverageInfo && (
                    <MDTypography
                      component="div"
                      sx={{
                        mt: 1,
                        fontSize: "0.8rem",
                        lineHeight: 1.6,
                        color: leverageInfo.leverage >= 10 ? COLORS.red : COLORS.grayDark,
                        fontWeight: leverageInfo.leverage >= 10 ? 700 : 400,
                      }}
                    >
                      {(LANGUAGE.Main.LeverageHint || "目前手數約等於名目 {notional}，是本金的 {lev} 倍（以最近一根日線收盤估算，即現在下單的槓桿；回測期間會隨當時價格不同）")
                        .replace("{notional}", "$" + Math.round(leverageInfo.notional).toLocaleString("en-US"))
                        .replace("{lev}", leverageInfo.leverage.toFixed(1))}
                      {leverageInfo.leverage >= 10 && (
                        <Box component="span" sx={{ display: "block" }}>
                          {/* 量化而非「小幅波動」：10 倍要逆向約 10% 才虧光，43 倍只要約 2.3%。
                              未計點差與隔夜利息，所以文案用「約」。 */}
                          {(LANGUAGE.Main.LeverageHigh || "槓桿偏高 —— 價格逆向變動約 {pct}% 就會虧掉全部本金")
                            .replace("{pct}", (100 / leverageInfo.leverage).toFixed(1))}
                        </Box>
                      )}
                      {/* 回測手數 ≠ EXE 實盤手數：EXE 登入時會夾到 capLev 倍內。不說的話，使用者會以為
                          回測的報酬/爆倉就是 EXE 會發生的事。自成一行、固定一般字重與灰字：
                          這是說明不是警告，不該跟著上面的紅字變紅。 */}
                      {leverageInfo.liveLots !== null && (() => {
                        const ratio = (leverageInfo.liveLots / Number(lotSize)) * 100;
                        return (
                          <Box component="span" sx={{ display: "block", mt: 0.5, color: COLORS.grayDark, fontWeight: 400 }}>
                            {(LANGUAGE.Main.LiveLotsNotice || "EXE 實盤會自動降到 {live} 手（本金的 {lev} 倍，上限 {cap} 倍）。回測是以 {lots} 手計算，實盤損益金額約為回測的 {ratio}%。實際手數依 EXE 啟動當下價格計算，可在 EXE 內調整。")
                              .replace("{live}", String(leverageInfo.liveLots))
                              .replace("{lev}", leverageInfo.liveLeverage.toFixed(1))
                              .replace("{cap}", String(leverageInfo.capLev))
                              .replace("{lots}", String(Number(lotSize)))
                              .replace("{ratio}", ratio >= 10 ? ratio.toFixed(0) : ratio.toFixed(1))}
                          </Box>
                        );
                      })()}
                    </MDTypography>
                  )}
                </Box>
              </Grid>

              {/* Right Column: Logic */}
              <Grid item xs={12} md={7}>
                <SectionHeader title={LANGUAGE.Main.SecLogic || "策略邏輯"} mb={1.5} />

                {/* Entry Condition */}
                <Box mb={2}>
                  <SectionLabel
                    title={LANGUAGE.Main.Content20 + "*"}
                    onClick={() => setOpenEntryDialog(true)}
                  />
                  <TagInput
                    value={entryCondition}
                    onChange={setEntryCondition}
                    placeholder={LANGUAGE.Main.Content23}
                    maxLength={EntryCondition_maxLength}
                    error={formSubmitted && entryCondition.length === 0}
                  />
                  <Box textAlign="right">
                    <MDTypography variant="caption" sx={{
                      color: entryCondition.map(v => `(${v})`).join("").length > EntryCondition_maxLength ? "red" : COLORS.grayMedian,
                      fontSize: "0.7rem",
                      fontWeight: "bold"
                    }}>
                      {entryCondition.map(v => `(${v})`).join("").length}/{EntryCondition_maxLength}
                    </MDTypography>
                  </Box>
                </Box>

                {/* Exit Condition */}
                <Box mb={2}>
                  <SectionLabel
                    title={LANGUAGE.Main.Content25 + "*"}
                    onClick={() => setOpenExitDialog(true)}
                  />
                  <TagInput
                    value={exitCondition}
                    onChange={setExitCondition}
                    placeholder={LANGUAGE.Main.Content26}
                    maxLength={ExitCondition_maxLength}
                    error={formSubmitted && exitCondition.length === 0}
                  />
                  <Box textAlign="right">
                    <MDTypography variant="caption" sx={{
                      color: exitCondition.map(v => `(${v})`).join("").length > ExitCondition_maxLength ? "red" : COLORS.grayMedian,
                      fontSize: "0.7rem",
                      fontWeight: "bold"
                    }}>
                      {exitCondition.map(v => `(${v})`).join("").length}/{ExitCondition_maxLength}
                    </MDTypography>
                  </Box>
                </Box>

                {/* Strategy Description */}
                <Box>
                  <SectionLabel
                    title={LANGUAGE.Main.Content27}
                    onClick={() => setOpenExtraDialog(true)}
                  />
                  <TagInput
                    value={strategyDescription}
                    onChange={setStrategyDescription}
                    placeholder={LANGUAGE.Main.Content28 || "例：策略說明內容..."}
                    maxLength={StrategyDescription_maxLength}
                  />
                  <Box textAlign="right">
                    <MDTypography variant="caption" sx={{
                      color: strategyDescription.map(v => `(${v})`).join("").length > StrategyDescription_maxLength ? "red" : COLORS.grayMedian,
                      fontSize: "0.7rem",
                      fontWeight: "bold"
                    }}>
                      {strategyDescription.map(v => `(${v})`).join("").length}/{StrategyDescription_maxLength}
                    </MDTypography>
                  </Box>
                </Box>
              </Grid>
            </Grid>

            <Box mt={2} width="100%">
              <MDButton
                fullWidth
                variant="contained"
                onClick={() => {
                  setFormSubmitted(true);
                  if (!symbol || !timeframe || !capital || !lotSize || !startDate || !endDate || entryCondition.length === 0 || exitCondition.length === 0) {
                    toast.error(LANGUAGE.Error.Content16);
                    return;
                  }
                  // 回測起始日期不能大於結束日期
                  if (dayjs(startDate).isAfter(dayjs(endDate))) {
                    toast.error(LANGUAGE.Error.DateRangeInvalid || "回測起始日期不能大於結束日期");
                    return;
                  }
                  // 本金最小 100，不得低於 100
                  if (Number(capital) < 100) {
                    toast.error(LANGUAGE.Error.MinCapital || "本金最低為 100，不得低於 100");
                    return;
                  }
                  // 手數最小 0.01，不得低於 0.01
                  if (Number(lotSize) < 0.01) {
                    toast.error(LANGUAGE.Error.MinLotSize || "手數最低為 0.01，不得低於 0.01");
                    return;
                  }
                  handleSubmit();
                }}
                disabled={isLoading}
                sx={{
                  background: "linear-gradient(90deg, #ffb347 0%, #f88e2b 100%)",
                  color: "#ffffff !important",
                  py: 1.8,
                  borderRadius: "20px",
                  fontSize: "1.1rem",
                  fontWeight: "bold",
                  boxShadow: "0 6px 15px 0 rgba(248, 142, 43, 0.4)",
                  "&:hover": {
                    background: "linear-gradient(90deg, #fbb040 0%, #d35400 100%)",
                    boxShadow: "0 8px 20px 0 rgba(248, 142, 43, 0.5)",
                  }
                }}
              >
                {isLoading ? (
                  <>
                    <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                    {LANGUAGE.Main.Content29}
                  </>
                ) : (
                  LANGUAGE.Main.Content38
                )}
              </MDButton>

              {/* 次數 / Coin 提示 —— 放在按鈕正下方（使用者視線焦點），不再只靠上方 Snackbar */}
              {renderQuotaArea("preview", retryPreviewWithCoin)}
            </Box>

            <Box id="summary-area-anchor" ref={summaryAreaRef}>
              {isThinking && (
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  py={10}
                  sx={{
                    animation: "fadeIn 0.5s ease-in-out",
                    backgroundColor: COLORS.white,
                    borderRadius: "24px",
                    mt: 4,
                    boxShadow: "0 12px 40px 0 rgba(0,0,0,0.06)",
                    border: "1px solid rgba(0,0,0,0.03)",
                  }}
                >
                  <img src="/images/Loading.gif" alt="Thinking" style={{ width: 120, height: "auto" }} />
                  <MDTypography variant="h5" sx={{ color: COLORS.grayMedian, mt: 2, fontWeight: "medium" }}>
                    Thinking ...
                  </MDTypography>
                </Box>
              )}

              {animatedSummary && (
                <Fade in={!!animatedSummary} timeout={800}>
                  <MDBox mt={4} sx={{ position: "relative" }}>
                    <Card
                      sx={{
                        p: 4,
                        borderRadius: "24px",
                        boxShadow: "0 12px 40px 0 rgba(0,0,0,0.06)",
                        backgroundColor: COLORS.white,
                        border: "1px solid rgba(0,0,0,0.03)",
                      }}
                    >
                      <Box display="flex" alignItems="center" mb={3}>
                        <Box sx={{ width: 4, height: 20, bgcolor: ORANGE_PRIMARY, mr: 1.5, borderRadius: "2px" }} />
                        <MDTypography variant="h6" fontWeight="bold" color="dark">
                          {LANGUAGE.Main.Content30}
                        </MDTypography>
                      </Box>

                      <Box sx={{ "& > div": { mb: 1.5 } }}>
                        {[
                          { label: LANGUAGE.Main.Content11, value: symbol },
                          { label: LANGUAGE.Main.Content12, value: timeframe },
                          { label: LANGUAGE.Main.Content13.replace(/ \(.*USD\)/i, ''), value: `${Number(capital).toLocaleString()} USD` },
                          { label: LANGUAGE.Main.Content37, value: lotSize },
                          { label: LANGUAGE.Main.Content15, value: orderType === "LONG" ? LANGUAGE.Main.Content16 : orderType === "SHORT" ? LANGUAGE.Main.Content17 : LANGUAGE.Main.Content41 },
                          { label: LANGUAGE.Main.SummaryStartShort || '起日', value: dayjs(startDate).format("YYYY-MM-DD") },
                          { label: LANGUAGE.Main.SummaryEndShort || '訖日', value: dayjs(endDate).format("YYYY-MM-DD") },
                        ].map((item, idx) => (
                          <Fade in={!!animatedSummary} timeout={500} style={{ transitionDelay: `${idx * 150}ms` }} key={idx}>
                            <Box display="flex" alignItems="center">
                              <MDTypography variant="button" fontWeight="bold" sx={{ color: ORANGE_PRIMARY, minWidth: "120px" }}>
                                {item.label}：
                              </MDTypography>
                              <MDTypography variant="button" fontWeight="bold" color="dark">
                                {item.value}
                              </MDTypography>
                            </Box>
                          </Fade>
                        ))}
                      </Box>

                      <Box mt={4} sx={{ "& > div": { mb: 2 } }}>
                        <Fade in={!!animatedSummary} timeout={500} style={{ transitionDelay: `${7 * 150}ms` }}>
                          <MDBox>
                            <MDTypography variant="button" fontWeight="bold" sx={{ color: ORANGE_PRIMARY, display: "block", mb: 0.5 }}>
                              {LANGUAGE.Main.Content20}：
                            </MDTypography>
                            <MDTypography variant="body2" color="dark" fontWeight="bold" sx={{ fontSize: "0.9rem" }}>
                              {entryCondition}
                            </MDTypography>
                          </MDBox>
                        </Fade>

                        <Fade in={!!animatedSummary} timeout={500} style={{ transitionDelay: `${8 * 150}ms` }}>
                          <MDBox>
                            <MDTypography variant="button" fontWeight="bold" sx={{ color: ORANGE_PRIMARY, display: "block", mb: 0.5 }}>
                              {LANGUAGE.Main.Content25}：
                            </MDTypography>
                            <MDTypography variant="body2" color="dark" fontWeight="bold" sx={{ fontSize: "0.9rem" }}>
                              {exitCondition || "-"}
                            </MDTypography>
                          </MDBox>
                        </Fade>

                        {/* 🛡️ 2026-09-29（A11）風控：AI 摘要裡「風控：」那一段（實際採用的停損）。
                            順序跟 AI 摘要一致（出場條件 → 風控 → 策略邏輯），並在下方停損說明之上。
                            解析不到（舊摘要、AI 沒照格式、拒答、DEMO）就不顯示這塊 —— 下方 StopLossNotice 已說明預設停損。 */}
                        {summaryRisk && (
                          <Fade in={!!animatedSummary} timeout={500} style={{ transitionDelay: `${8.5 * 150}ms` }}>
                            <MDBox>
                              <MDTypography variant="button" fontWeight="bold" sx={{ color: ORANGE_PRIMARY, display: "block", mb: 0.5 }}>
                                {LANGUAGE.Main.SecRiskControl || "風控（停損）"}：
                              </MDTypography>
                              <MDTypography
                                variant="body2"
                                color="dark"
                                fontWeight="bold"
                                sx={{ whiteSpace: "pre-wrap", lineHeight: 1.6, fontSize: "0.9rem" }}
                              >
                                {summaryRisk}
                              </MDTypography>
                            </MDBox>
                          </Fade>
                        )}

                        <Fade in={!!animatedSummary} timeout={500} style={{ transitionDelay: `${9 * 150}ms` }}>
                          <MDBox>
                            <MDTypography variant="button" fontWeight="bold" sx={{ color: ORANGE_PRIMARY, display: "block", mb: 0.5 }}>
                              {LANGUAGE.Main.Content19}：
                            </MDTypography>
                            <MDTypography
                              variant="body2"
                              color="dark"
                              fontWeight="bold"
                              sx={{
                                whiteSpace: "pre-wrap",
                                lineHeight: 1.6,
                                textAlign: "justify",
                                fontSize: "0.9rem"
                              }}
                            >
                              {animatedSummary}
                              {isTyping && cursorVisible ? <span style={{ color: ORANGE_PRIMARY }}>|</span> : null}
                              <Box ref={animatedRef} sx={{ height: 0, visibility: "hidden" }} />
                            </MDTypography>
                          </MDBox>
                        </Fade>
                      </Box>

                      {demoMode && (
                        <MDTypography sx={{ display: "block", textAlign: "center", color: COLORS.grayDark, fontStyle: "italic", fontSize: "0.8rem", mt: 2 }}>
                          ⚠️ {LANGUAGE.Main.DemoNotice || "本頁為產品體驗 DEMO，真實 AI 程式生成、回測與一鍵部署請前往 MasQuant 平台使用"}
                        </MDTypography>
                      )}

                      {/* 🛡️ 2026-09-21 停損說明。生成 prompt 要求每支策略都要有價格停損
                          （server/services/strategyService.js「停損 — 強制實作」，8/27 定的「沒停損不准打包」）。
                          實例：使用者寫「不出場、一直持有黃金」，實際跑的帶了停損 → 停損出場後爆倉，他完全不知道。
                          （2026-09-29 起 AI 摘要的「風控」段會顯示在上方「風控（停損）」區塊；這段說明保留，
                            因為摘要解析不到風控時它是唯一的說明，而且它講的是回測程式的生成規則，不是這支策略的停損值。）
                          ⚠️ 措辭刻意不寫成保證：回測路徑上停損**只靠 prompt 要求 AI 寫**，沒有程式碼檢查或補上；
                          唯一的程式層檢查在打包 EXE 時（assertStopLossOrThrow，且只比對寫法、管理員可放行）。
                          條件也照 prompt 原文：「使用者沒有指定」才用預設 2×ATR，以每根 K 棒收盤判定。
                          固定說明，不需要改 prompt、也不用 bump 快取版本。
                          字色用黑色：這段的存在目的就是「要被看見」，灰字在淺灰底只有約 3.2:1。 */}
                      {strategySummary && (
                        <MDTypography
                          component="div"
                          sx={{
                            mt: 4,
                            px: 2,
                            py: 1.25,
                            borderRadius: "10px",
                            fontSize: "0.8rem",
                            lineHeight: 1.7,
                            color: COLORS.black,
                            bgcolor: COLORS.grayLightest,
                          }}
                        >
                          {LANGUAGE.Main.StopLossNotice ||
                            "🛡️ 產生回測程式時，平台會要求 AI 為每支策略加入價格停損（即使出場條件寫「不出場」也一樣）。若您沒有指定停損，預設為：做多 進場價 − 2×ATR(14)、做空 進場價 + 2×ATR(14)，以每根 K 棒收盤價判定。打包成 EXE 前系統會檢查程式碼，偵測不到停損就不予打包。"}
                        </MDTypography>
                      )}

                      {strategySummary && (
                        <MDBox mt={3} ref={backtestBtnRef}>
                          <MDButton
                            fullWidth
                            variant="gradient"
                            onClick={handleBacktest}
                            disabled={isLoading || isTyping}
                            sx={{
                              background: ORANGE_GRADIENT,
                              color: COLORS.white,
                              py: 1.8,
                              borderRadius: "12px",
                              fontSize: "1.1rem",
                              fontWeight: "bold",
                              boxShadow: "0 8px 25px 0 rgba(248, 142, 43, 0.3)",
                              "&:hover": {
                                background: ORANGE_GRADIENT,
                                opacity: 0.9,
                              },
                              "&.Mui-disabled": {
                                background: COLORS.grayMedian,
                                color: COLORS.white
                              }
                            }}
                          >
                            {isLoading ? (
                              <Box display="flex" alignItems="center">
                                <CircularProgress size={24} color="inherit" sx={{ mr: 2 }} />
                                <MDTypography variant="button" color="inherit" fontWeight="bold">
                                  {stageText}
                                </MDTypography>
                              </Box>
                            ) : (
                              LANGUAGE.Main.Content32
                            )}
                          </MDButton>

                          {/* 次數 / Coin 提示 —— 放在按鈕正下方（使用者視線焦點），不再只靠上方 Snackbar */}
                          {renderQuotaArea("backtest", retryBacktestWithCoin)}
                        </MDBox>
                      )}

                      {emptyBacktestErrorMessage && (
                        <MDBox mt={3}>
                          <Alert severity="warning" sx={{ width: "100%", bgcolor: "rgba(255, 152, 0, 0.1)", color: COLORS.main, fontWeight: "medium", "& .MuiAlert-message": { whiteSpace: "pre-line", lineHeight: 1.8 } }}>
                            {emptyBacktestErrorMessage}
                          </Alert>
                        </MDBox>
                      )}
                    </Card>
                  </MDBox>
                </Fade>
              )}
            </Box>




          </Card>

          <Dialog
            open={kpiDialogOpen}
            onClose={(_, reason) => {
              if (reason !== "backdropClick") {
                setKpiDialogOpen(false);
              }
            }}
            fullWidth
            maxWidth="md"
            PaperProps={{
              sx: {
                borderRadius: "24px",
                p: 1,
                overflow: "hidden",
                boxShadow: "0 24px 80px rgba(0,0,0,0.15)",
              },
            }}
          >
            <Box display="flex" justifyContent="space-between" alignItems="center" px={3} py={2}>
              <MDTypography variant="h5" fontWeight="bold" color="dark">
                {LANGUAGE.Main.Content42 || "策略分析報告"}
              </MDTypography>
              <IconButton
                onClick={() => setKpiDialogOpen(false)}
                sx={{
                  color: COLORS.grayMedian,
                  "&:hover": { color: COLORS.black, backgroundColor: "rgba(0,0,0,0.05)" }
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
            <DialogContent dividers sx={{ borderTop: "1px solid rgba(0,0,0,0.05) !important", p: 0 }}>
              <Box sx={{ minHeight: "400px", position: "relative", backgroundColor: "#fdfdfd" }}>
                {isLoading && (
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    sx={{
                      position: "absolute",
                      inset: 0,
                      zIndex: 10,
                      backgroundColor: COLORS.white,
                      animation: "fadeIn 0.3s ease-in-out",
                    }}
                  >
                    <img src="/images/Loading.gif" alt="Thinking" style={{ width: 140, height: "auto" }} />
                    <MDTypography variant="h5" sx={{ color: COLORS.grayMedian, mt: 2, fontWeight: "medium" }}>
                      Thinking ...
                    </MDTypography>
                  </Box>
                )}

                {/* 2026-06-13：回測無結果（沒訊號／跑不動／額度不足…）→ 在報告 dialog 內顯示原因，
                    不要留白（dialog 是 modal、會蓋住底部那條提示，使用者看不到）。errorMessage 已涵蓋所有失敗類型，含可點的額度/coin 提示。 */}
                {!isLoading && !backtestJson && errorMessage && (
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    sx={{ minHeight: "400px", p: 4, textAlign: "center" }}
                  >
                    <Box sx={{ fontSize: 52, mb: 1.5, lineHeight: 1 }}>⚠️</Box>
                    <Box sx={{ color: COLORS.grayDark, maxWidth: 520, lineHeight: 1.8, fontSize: "15px", whiteSpace: "pre-line" }}>
                      {errorMessage}
                    </Box>
                  </Box>
                )}

                {backtestJson && (
                  <Fade in={!!backtestJson} timeout={600}>
                    <Box sx={{ p: 3 }}>
                      <StrategyKPICharts data={backtestJson} showBankruptNotice />

                      {demoMode && (
                        <MDTypography sx={{ display: "block", textAlign: "center", color: COLORS.grayDark, fontStyle: "italic", fontSize: "0.8rem", mt: 3 }}>
                          ⚠️ {LANGUAGE.Main.DemoNotice || "本頁為產品體驗 DEMO，真實 AI 程式生成、回測與一鍵部署請前往 MasQuant 平台使用"}
                        </MDTypography>
                      )}

                      {demoMode ? (
                        <MDBox mt={3} textAlign="center">
                          <MDButton
                            variant="gradient"
                            onClick={handleSaveStrategy}
                            sx={{ background: ORANGE_GRADIENT, color: COLORS.white, py: 1.5, px: 4, borderRadius: "12px", boxShadow: "0 8px 20px rgba(248, 142, 43, 0.2)", "&:hover": { background: ORANGE_GRADIENT, opacity: 0.9 } }}
                          >
                            {LANGUAGE.Main.DemoCTA || "前往 MasQuant 平台部署"}
                          </MDButton>
                        </MDBox>
                      ) : (
                        <MDBox mt={4} p={3} sx={{ backgroundColor: COLORS.white, borderRadius: "16px", border: "1px solid rgba(0,0,0,0.05)" }}>
                          <MDTypography variant="h6" fontWeight="bold" gutterBottom color="dark">
                            {LANGUAGE.Main.Content33}
                          </MDTypography>
                          <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} sm={8}>
                              <TextField
                                fullWidth
                                placeholder={LANGUAGE.Main.Content34}
                                value={strategyTitle}
                                variant="outlined"
                                onChange={(e) => setStrategyTitle(e.target.value)}
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    borderRadius: "12px",
                                  },
                                }}
                              />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                              <MDButton
                                fullWidth
                                variant="gradient"
                                onClick={handleSaveStrategy}
                                disabled={isLoading || !strategyTitle}
                                sx={{
                                  background: ORANGE_GRADIENT,
                                  color: COLORS.white,
                                  py: 1.5,
                                  borderRadius: "12px",
                                  boxShadow: "0 8px 20px rgba(248, 142, 43, 0.2)",
                                  "&:hover": {
                                    background: ORANGE_GRADIENT,
                                    opacity: 0.9,
                                  },
                                }}
                              >
                                {isLoading ? <CircularProgress size={20} color="inherit" /> : LANGUAGE.Main.Content35}
                              </MDButton>
                            </Grid>
                          </Grid>
                        </MDBox>
                      )}
                    </Box>
                  </Fade>
                )}
              </Box>
            </DialogContent>
          </Dialog>

          <Snackbar
            open={successOpen}
            autoHideDuration={4000}
            onClose={() => setSuccessOpen(false)}
          >
            <Alert onClose={() => setSuccessOpen(false)} severity="success" sx={{ width: "100%" }}>
              {LANGUAGE.Main.Content36}
            </Alert>
          </Snackbar>

          <Snackbar
            open={!!errorMessage}
            autoHideDuration={5000}
            onClose={() => setErrorMessage("")}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
          >
            <Alert severity="error" onClose={() => setErrorMessage("")} sx={{ width: "100%", "& .MuiAlert-message": { whiteSpace: "pre-line" } }}>
              {errorMessage}
            </Alert>
          </Snackbar>
          <ExampleFormulaDialog
            open={openEntryDialog}
            onClose={() => setOpenEntryDialog(false)}
            fieldType="BUY"
            demoMode={demoMode}
            localExamples={demoLogicExamples.examples}
            initialSelected={entryCondition}
            onApply={(selectedList) => {
              setEntryCondition(selectedList);
            }}
          />

          <ExampleFormulaDialog
            open={openExitDialog}
            onClose={() => setOpenExitDialog(false)}
            fieldType="SELL"
            demoMode={demoMode}
            localExamples={demoLogicExamples.examples}
            initialSelected={exitCondition}
            onApply={(selectedList) => {
              setExitCondition(selectedList);
            }}
          />

          <ExampleFormulaDialog
            open={openExtraDialog}
            onClose={() => setOpenExtraDialog(false)}
            fieldType="EXTRA"
            demoMode={demoMode}
            localExamples={demoLogicExamples.examples}
            initialSelected={strategyDescription}
            onApply={(selectedList) => {
              setStrategyDescription(selectedList);
            }}
          />

          <StrategyGroupDialog
            open={openStrategyGroupDialog}
            onClose={() => setOpenStrategyGroupDialog(false)}
            onSelect={(group) => handleInsertStrategyGroup(group)}
            orderTypeFilter={orderType === "LONG" ? "ASK" : orderType === "SHORT" ? "BID" : ""}
            demoMode={demoMode}
            localGroups={demoLogicGroups.groups}
          />

        </MDBox>
      </Fade>
    </>
  );
}
